// built-from: /value-stream-mapping-workspace v1 impl:value-stream-mapping-workspace role:implementation
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import StreamMappingWorkspace from '../components/StreamMappingWorkspace';
import { FacilitatorLauncher } from '../components/FacilitatorDock';
import { lumenGridModernisationJourney } from '../fixtures/modernisation/scenarios';
import { useDiscoveryPlan } from '../hooks/useDiscoveryPlan';
import { useSession } from '../hooks/useSession';
import { useEngagement } from '../hooks/useEngagement';
import { useStreamAssets } from '../hooks/useStreamAssets';
import { readScope } from '../utils/scope';
import type { StreamWorkspaceStream } from '../components/streamMappingWorkspaceTypes';
import './engagementJourney.css';

function trustedReturnPath(value: string | null): string | null {
  return value?.startsWith('/engagement/') ? value : null;
}

export function ValueStreamMappingActivityWorkspace(): JSX.Element {
  const { streamId } = useParams<{ streamId: string }>();
  const { search } = useLocation();
  const stream = (readScope().areas as StreamWorkspaceStream[]).find(s => s.id === streamId);
  const { currentEngagementId } = useEngagement();
  const assets = useStreamAssets(currentEngagementId, stream?.mappingSessionId);
  const context = `${currentEngagementId || ''}:${streamId}:${search}`;
  const [startedContext, setStartedContext] = useState('');
  if (stream?.sharedProcesses?.processIds.length) {
    const query = new URLSearchParams(search);
    query.set('process', query.get('process') || stream.sharedProcesses.processIds[0]);
    query.set('method', 'value-stream-mapping'); query.set('stream', stream.id);
    return <Navigate replace to={`/engagement/business-map?${query}`} />;
  }
  if (startedContext === context) return <LegacyValueStreamMappingActivityWorkspace />;
  const query = new URLSearchParams(search);
  const returnTo = trustedReturnPath(query.get('returnTo'));
  const activity = lumenGridModernisationJourney.activities.find(candidate => candidate.definition.id === query.get('activity'));
  const original = assets.streams.find(candidate => candidate.id === streamId) || stream;
  return <main className="business-workspace">
    {returnTo ? <Link to={returnTo}>Back to activity</Link> : null}
    {activity ? <strong>{activity.definition.name}</strong> : null}
    <p>Original value stream mapping work. Browsing does not start a session.</p>
    <Link to="/engagement/business-map">Business map</Link>
    <FacilitatorLauncher opening={false} onOpen={() => setStartedContext(context)} />
    {assets.error ? <p role="alert">{assets.error}</p> : null}
    <StreamMappingWorkspace streamOverride={original} sessionSnapshot={assets.session} readOnly />
  </main>;
}

function LegacyValueStreamMappingActivityWorkspace(): JSX.Element {
  const navigate = useNavigate();
  const { streamId } = useParams<{ streamId: string }>();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const returnTo = trustedReturnPath(query.get('returnTo'));
  const activityDefinitionId = query.get('activity');
  const canonicalActivity = lumenGridModernisationJourney.activities.find(
    (candidate) => candidate.definition.id === activityDefinitionId,
  );
  const hasContext = Boolean(returnTo && activityDefinitionId);
  const streams = readScope().areas as StreamWorkspaceStream[];
  const stream = useMemo(
    () => streams.find((candidate) => candidate.id === streamId) ?? null,
    [streamId, streams],
  );
  const session = useSession(stream?.mappingSessionId, 'discovery');
  const {
    plan,
    loading: discoveryPlanLoading,
    pendingActivityCommand,
    activityOutputError,
    linkActivitySession,
    returnActivityOutput,
  } = useDiscoveryPlan();
  const durableActivity = plan?.activities.find(
    (candidate) => candidate.id === activityDefinitionId
      || candidate.definition.id === activityDefinitionId,
  );
  const sessionId = session.sessionId ?? stream?.mappingSessionId ?? null;
  const linked = Boolean(sessionId && durableActivity?.sessionIds?.includes(sessionId));
  const latestOutput = durableActivity?.outputs?.find((candidate) => candidate.id === 'current-value-stream-model');
  const latestVersion = latestOutput?.versions.at(-1);
  const correcting = latestVersion?.state === 'changes-requested';
  const returnIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasContext || !durableActivity || !sessionId || linked || discoveryPlanLoading) return;
    void linkActivitySession(durableActivity.id, sessionId);
  }, [
    discoveryPlanLoading,
    durableActivity,
    hasContext,
    linked,
    linkActivitySession,
    sessionId,
  ]);

  const finishAndReturn = async (): Promise<void> => {
    if (!durableActivity || !stream || !sessionId || !session.newModel.flowText.trim()) return;
    if (!returnIdRef.current) {
      returnIdRef.current = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${durableActivity.id}-${sessionId}-${latestOutput?.versions.length ?? 0}-${Date.now()}`;
    }

    const saved = await returnActivityOutput(durableActivity.id, {
      sessionId,
      returnId: returnIdRef.current,
      returnedAt: new Date().toISOString(),
    });

    if (saved && returnTo) navigate(returnTo);
  };

  return (
    <div className={`journey-specialist-workspace${hasContext ? ' journey-specialist-workspace--contextual' : ''}`}>
      {hasContext ? (
        <div className="journey-specialist-context" aria-label="Activity context">
          <div>
            <span>Activity workspace</span>
            <strong>{durableActivity?.definition.name ?? canonicalActivity?.definition.name ?? 'Current-state mapping'}</strong>
          </div>
          <div className="journey-specialist-context__actions">
            <Link to={returnTo ?? '/engagement/discovery'}>Back to activity</Link>
            {durableActivity && sessionId ? (
              <button
                type="button"
                className="journey-button journey-button--primary"
                disabled={discoveryPlanLoading
                  || session.sessionLoading
                  || pendingActivityCommand !== null
                  || !session.newModel.flowText.trim()}
                onClick={() => void finishAndReturn()}
              >
                {pendingActivityCommand === 'return'
                  ? 'Returning output…'
                  : correcting
                    ? 'Return corrected output'
                    : 'Finish and return output'}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {hasContext && activityOutputError ? (
        <div className="journey-specialist-error" role="alert">
          {activityOutputError.message}
        </div>
      ) : null}
      <StreamMappingWorkspace />
    </div>
  );
}
